// Verified runs (roadmap Phase 2): replay seed + recorded commands with the same sim and
// compare a digest of the result with what the client claims. Runs on the server.

import { Engine } from "./engine";
import { netWorth } from "./player";
import type { GameState, RunRecord } from "./state";

export interface RunSummary {
  tick: number;
  netWorth: number;
  followers: number;
  trust: number;
  rank: number;
  cash: number;
  bestTrade: number;
  bestLaunchMcap: number;
  rugs: number;
  origin: string;
  digest: string;
}

/** FNV-1a over a canonical JSON string (stable key order from the sim's own object layout). */
function fnv(str: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h.toString(16).padStart(8, "0");
}

/** Digest of everything a cheater would want to change: the player and their positions. */
export function stateDigest(state: GameState): string {
  const p = state.player;
  const positions = Object.values(state.market.tokens)
    .filter((t) => (t.holders["player"] ?? 0) > 0)
    .map((t) => [t.id, Math.round(t.holders["player"])]);
  const core = { tick: state.tick, cash: Math.round(p.cash * 100), dgn: Math.round(p.dgn * 100), followers: p.followers, trust: Math.round(p.trust * 100), heat: Math.round(p.heat * 100), positions, cars: state.cars.owned.length, houses: state.houses.owned.map((h) => h.id) };
  return fnv(JSON.stringify(core));
}

export function summarize(state: GameState): RunSummary {
  const p = state.player;
  return {
    tick: state.tick,
    netWorth: Math.round(netWorth(state)),
    followers: p.followers,
    trust: Math.round(p.trust),
    rank: p.rank,
    cash: Math.round(p.cash),
    bestTrade: Math.round(p.stats.bestTradeUsd),
    bestLaunchMcap: Math.round(Math.max(0, ...p.launches.map((l) => state.market.tokens[l.tokenId]?.peakMcap ?? 0))),
    rugs: p.stats.rugsPulled,
    origin: p.origin,
    digest: stateDigest(state),
  };
}

/** Rebuild a run from its seed and command log, up to `untilTick`. */
export function replayRun(seed: number, run: RunRecord, untilTick: number): Engine {
  const e = Engine.newGame(seed, structuredClone(run.input));
  for (const [tick, cmd] of run.actions) {
    if (tick > untilTick) break;
    if (tick > e.state.tick) e.advance(tick - e.state.tick);
    e.dispatch(structuredClone(cmd));
  }
  if (untilTick > e.state.tick) e.advance(untilTick - e.state.tick);
  return e;
}

export type Verdict = { ok: true; summary: RunSummary } | { ok: false; reason: string; expected?: RunSummary };

/** Server-side check: does replaying the log reproduce the claimed result? */
export function verifyRun(seed: number, run: RunRecord, claimed: RunSummary): Verdict {
  if (run.legacy) return { ok: false, reason: "legacy save (recorded before run logs)" };
  let e: Engine;
  try {
    e = replayRun(seed, run, claimed.tick);
  } catch (err) {
    return { ok: false, reason: `replay crashed: ${String(err).slice(0, 120)}` };
  }
  const got = summarize(e.state);
  if (got.digest !== claimed.digest || got.netWorth !== claimed.netWorth || got.followers !== claimed.followers) return { ok: false, reason: "replay does not match the claimed state", expected: got };
  return { ok: true, summary: got };
}
